/**
 * Every finding for a store.
 *
 * WHY the by_kind chips come first: they are the engine's own grouping (kind + severity +
 * adjustment class) and answer "what kind of problem do I have" before the seller has to read
 * a single item. The status filter exists because a finding is never deleted — it moves through
 * OPEN → ACKNOWLEDGED → RESOLVED / IGNORED, and the history is the audit trail.
 */
import { useState, type ReactNode } from "react";
import { api, DISCREPANCY_STATUSES, type DiscrepancyStatus } from "../api";
import { Amount } from "../components/Amount";
import { SeverityBadge } from "../components/Badges";
import { DiscrepancyList } from "../components/DiscrepancyList";
import { EmptyState, ErrorState, Loading } from "../components/States";
import { formatCount } from "../money";
import { useAsync } from "../hooks/useAsync";
import { useStore } from "./StoreLayout";

const EMPTY_HINT: Readonly<Record<DiscrepancyStatus, string>> = {
  OPEN: "Nothing unexplained. Every settlement line this store has synced reconciles to its order and its declared totals.",
  ACKNOWLEDGED: "No findings have been acknowledged yet.",
  RESOLVED: "No findings have been resolved yet.",
  IGNORED: "No findings have been ignored.",
};

export function Discrepancies(): ReactNode {
  const store = useStore();
  const [status, setStatus] = useState<DiscrepancyStatus>("OPEN");
  const state = useAsync(() => api.discrepancies(store.id, { status }), [store.id, status]);

  return (
    <>
      <header className="page-head">
        <h1 className="page-title">Discrepancies</h1>
        <div className="filters" role="group" aria-label="Status filter">
          {DISCREPANCY_STATUSES.map((s) => (
            <button key={s} type="button" className={`filter${s === status ? " filter-active" : ""}`} onClick={() => setStatus(s)}>{s}</button>
          ))}
        </div>
      </header>

      {state.status === "loading" ? <Loading label="Loading findings" /> : null}
      {state.status === "error" ? <ErrorState error={state.error} onRetry={state.reload} /> : null}
      {state.status === "ready" ? (
        state.data.items.length === 0 ? (
          <EmptyState title={`No ${status} discrepancies`} hint={EMPTY_HINT[status]} />
        ) : (
          <>
            <ul className="chips">
              {state.data.by_kind.map((g) => (
                <li key={`${g.kind}-${g.severity}-${g.adjustment_class ?? ""}-${g.currency ?? ""}`} className="summary-chip">
                  <SeverityBadge severity={g.severity} />
                  <span className="summary-chip-kind">{g.kind}</span>
                  {g.adjustment_class ? <span className="chip">{g.adjustment_class}</span> : null}
                  <span className="summary-chip-count">{formatCount(g.count)}</span>
                  {g.amount_minor !== null ? <Amount minor={g.amount_minor} currency={g.currency} signed /> : null}
                </li>
              ))}
            </ul>
            <DiscrepancyList items={state.data.items} storeId={store.id} />
          </>
        )
      ) : null}
    </>
  );
}
